import { Select } from "antd";
import classNames from "classnames";
import { get, isEmpty, isFunction, isObject } from "lodash";
import Label from "shared/components/Label";
import { useQuery } from "shared/hooks/useApi";

const Async = ({
  label: selectLabel,
  node,
  errors,
  icon,
  labels,
  onChange = () => {},
  onSearch = () => {},
  touched,
  required,
  optional,
  filter,
  optionFilterProp = "label",
  ...rest
}) => {
  const {
    query,
    variables,
    accessors: { root, label = "name", value = "id" },
    fallback = [],
  } = node;
  const resolveAccessorKey = (item, key, separator) => {
    const [principal, alt] = key.split(separator);
    const principalValue = get(item, principal);
    if (principalValue !== undefined && principalValue !== null) {
      return principalValue;
    }
    if (alt) {
      const altValue = get(item, alt);
      if (altValue !== undefined && altValue !== null) {
        return altValue;
      }
    }
    return "";
  };

  const getLabel = (item) => {
    if (!isEmpty(labels)) return labels[get(item, label)];
    if (isFunction(label)) return label(item);
    if (isObject(label)) {
      const separator = get(label, "separator", "-");
      return get(label, "keys", [])
        .map((key) => resolveAccessorKey(item, key, separator))
        .join(separator);
    }
    return get(item, label);
  };
  const { data, loading } = useQuery(query, { variables });

  const sourceItems = isEmpty(get(data, root, []))
    ? fallback
    : get(data, root, []);
  const items = sourceItems.map((item) => ({
    label: getLabel(item),
    value: get(item, value).toString(),
  }));
  const byLabel = (left, right) =>
    String(left || "").localeCompare(String(right || ""), "fr", {
      numeric: true,
      sensitivity: "base",
    });
  const listedItems = filter ? items.filter(filter) : items;
  const options = node.sortByLabel
    ? [...listedItems].sort((left, right) => byLabel(left.label, right.label))
    : listedItems;
  const { value: selectedValue, ...selectProps } = rest;
  const labelByValue = options.reduce((names, option) => {
    names[option.value] = option.label;
    return names;
  }, {});
  const sortIds = (ids) =>
    [...ids].sort((left, right) =>
      byLabel(labelByValue[String(left)], labelByValue[String(right)]),
    );
  const displayedValue =
    node.sortByLabel && Array.isArray(selectedValue)
      ? sortIds(selectedValue)
      : selectedValue;

  return (
    <div
      className={classNames("select__wrapper", { __error: touched && errors })}
    >
      <Label
        label={selectLabel}
        icon={icon}
        required={required}
        optional={optional}
      />
      <div className={`select__wrapper--field`}>
        <Select
          size="large"
          className={classNames({ "disable--input": rest.disabled })}
          loading={loading}
          onChange={(nextValue) => {
            const sortedValue =
              node.sortByLabel && Array.isArray(nextValue)
                ? sortIds(nextValue)
                : nextValue;
            return onChange(
              sortedValue,
              sourceItems.find(
                (item) => String(get(item, value)) === String(sortedValue),
              ),
            );
          }}
          showSearch
          optionFilterProp={optionFilterProp}
          onSearch={onSearch}
          options={options}
          {...selectProps}
          value={displayedValue}
        />
        {touched && errors && <p className={"input--errors"}>{errors}</p>}
      </div>
    </div>
  );
};

export default Async;
